import { useCallback, useEffect, useState } from "react";

import { api } from "~/api";
import { Button } from "~/components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/Dialog";
import { SessionExpired } from "~/components/SessionExpired";
import { useAuth } from "~/contexts/auth";
import type { Billing2Migration, BillingStorageGap } from "~/types";
import { formatUtcTimestamp } from "~/utils/time";

function formatTime(value: string | null): string {
  return value ? formatUtcTimestamp(value) : "—";
}

function formatQuantity(value: number, suffix: string): string {
  const units = ["", "K", "M", "G", "T", "P"];
  let unit = 0;
  let scaled = value;
  while (Math.abs(scaled) >= 1000 && unit < units.length - 1) {
    scaled /= 1000;
    unit += 1;
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(scaled)} ${units[unit]}${suffix}`;
}

function formatDuration(start: string, end: string): string {
  const minutes = Math.max(
    0,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000),
  );
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return hours > 0 ? `${hours}h ${remainingMinutes}m` : `${minutes}m`;
}

function formatAge(value: string): string {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 60000),
  );
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const remainingMinutes = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${remainingMinutes}m`;
  return `${minutes}m`;
}

function pipeline(row: Billing2Migration): {
  label: string;
  className: string;
} {
  if (!row.cutover_at) {
    return {
      label: "Billing1",
      className:
        "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200",
    };
  }
  if (row.rollback_at) {
    return {
      label: "Reverted",
      className:
        "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200",
    };
  }
  return {
    label: "Billing2",
    className:
      "border-green-300 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-950 dark:text-green-200",
  };
}

export default function BillingMigrationPage() {
  const [rows, setRows] = useState<Billing2Migration[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [storageGapPreview, setStorageGapPreview] =
    useState<BillingStorageGap | null>(null);
  const [cutoverTarget, setCutoverTarget] = useState<Billing2Migration | null>(
    null,
  );
  const [rollbackTarget, setRollbackTarget] =
    useState<Billing2Migration | null>(null);
  const { status: authStatus, setIsAuthorized } = useAuth();

  const refresh = useCallback(async () => {
    try {
      setRows(await api.getBilling2Migrations());
      setRefreshError(null);
      setIsAuthorized(true);
    } catch (err) {
      if (err instanceof Error && "status" in err && err.status === 401) {
        setIsAuthorized(false);
      } else {
        setRefreshError(err instanceof Error ? err.message : "Request failed");
      }
    }
  }, [setIsAuthorized]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  const run = async (
    row: Billing2Migration,
    action: "cutover" | "rollback",
  ) => {
    setBusy(row.cluster_id);
    setActionError(null);
    try {
      if (action === "cutover") await api.setBilling2Cutover(row.cluster_id);
      if (action === "rollback") await api.setBilling2Rollback(row.cluster_id);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(null);
    }
  };

  const previewStorageGap = async (row: Billing2Migration) => {
    setBusy(row.cluster_id);
    setActionError(null);
    try {
      setStorageGapPreview(
        await api.mintBilling2StorageGap(row.cluster_id, true),
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(null);
    }
  };

  const mintStorageGap = async () => {
    if (!storageGapPreview) return;
    const clusterId = storageGapPreview.cluster_id;
    setBusy(clusterId);
    setActionError(null);
    try {
      await api.mintBilling2StorageGap(clusterId, false);
      setStorageGapPreview(null);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(null);
    }
  };

  if (authStatus !== "authorized") return <SessionExpired />;

  const storageGapTotals = storageGapPreview?.events.reduce(
    (totals, { event }) => ({
      bytes: totals.bytes + event.namespace_bytes,
      byteHours: totals.byteHours + (event.byte_hours ?? 0),
    }),
    { bytes: 0, byteHours: 0 },
  );

  return (
    <main className="w-full py-6">
      <h1 className="mb-4 text-xl font-semibold">billing2 migration</h1>
      {(actionError ?? refreshError) ? (
        <p className="mb-4 text-sm text-red-600">
          {actionError ?? refreshError}
        </p>
      ) : null}
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-left text-xs">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className="px-2 py-2 font-medium">Cluster</th>
              <th className="px-2 py-2 font-medium">Pipeline</th>
              <th className="px-2 py-2 font-medium">Cutover time</th>
              <th className="px-2 py-2 font-medium">
                First billing2 storage event
              </th>
              <th className="px-2 py-2 font-medium">Storage gap</th>
              <th className="px-2 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const disabled = busy === row.cluster_id;
              const pipelineStatus = pipeline(row);
              return (
                <tr key={row.cluster_id} className="border-b last:border-0">
                  <td className="whitespace-nowrap px-2 py-2 font-mono">
                    {row.cluster_id}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2">
                    <span
                      className={`${pipelineStatus.className} inline-flex rounded-full border px-2 py-0.5 font-medium`}
                    >
                      {pipelineStatus.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2 py-2">
                    <div>{formatTime(row.cutover_at)}</div>
                    {row.rollback_at ? (
                      <div className="text-[11px] text-muted-foreground">
                        rolled back {formatTime(row.rollback_at)}
                      </div>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2">
                    <div>{formatTime(row.billing2_storage_start_at)}</div>
                    {row.billing2_storage_start_observed_at ? (
                      <div className="text-[11px] text-muted-foreground">
                        unchanged since{" "}
                        {formatTime(row.billing2_storage_start_observed_at)}
                      </div>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2">
                    {row.cutover_at ? (
                      <span
                        className={`${
                          row.storage_gap_minted_at
                            ? "border-green-300 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-950 dark:text-green-200"
                            : "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
                        } inline-flex rounded-full border px-2 py-0.5 font-medium`}
                      >
                        {row.storage_gap_minted_at ? "Minted" : "Not minted"}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="flex min-w-max gap-1 px-2 py-2">
                    {!row.cutover_at || row.rollback_at ? (
                      <Button
                        size="sm"
                        className="h-8 px-2 text-[11px]"
                        disabled={disabled}
                        onClick={() => setCutoverTarget(row)}
                      >
                        {row.rollback_at
                          ? "Cut over to billing2 again"
                          : "Cut over to billing2"}
                      </Button>
                    ) : null}
                    {row.cutover_at && !row.rollback_at ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2 text-[11px]"
                        disabled={
                          disabled ||
                          !row.billing2_storage_start_at ||
                          row.storage_gap_minted_at !== null
                        }
                        onClick={() => void previewStorageGap(row)}
                      >
                        {row.storage_gap_minted_at
                          ? "Storage gap minted"
                          : "Mint storage gap"}
                      </Button>
                    ) : null}
                    {row.cutover_at && !row.rollback_at ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-8 px-2 text-[11px]"
                        disabled={disabled}
                        onClick={() => setRollbackTarget(row)}
                      >
                        Rollback to billing1
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Dialog
        open={storageGapPreview !== null}
        onOpenChange={(open) => !open && setStorageGapPreview(null)}
      >
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden md:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Storage gap preview</DialogTitle>
            <DialogDescription>
              {storageGapPreview
                ? `Review what will be billed for ${storageGapPreview.cluster_id}. Nothing has been minted yet.`
                : null}
            </DialogDescription>
          </DialogHeader>
          {storageGapPreview && storageGapTotals ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-auto pr-1 text-sm">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    Organizations
                  </div>
                  <div className="mt-1 text-lg font-semibold">
                    {storageGapPreview.events.length.toLocaleString()}
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    First billing2 event
                  </div>
                  <div className="mt-1 font-medium">
                    {formatTime(storageGapPreview.gap_end_at)}
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    Billable storage represented
                  </div>
                  <div className="mt-1 text-lg font-semibold">
                    {formatQuantity(storageGapTotals.bytes, "B")}
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">
                    Backfill to mint
                  </div>
                  <div className="mt-1 text-lg font-semibold">
                    {formatQuantity(storageGapTotals.byteHours, "B-hours")}
                  </div>
                </div>
              </div>
              {storageGapPreview.billing2_storage_start_observed_at ? (
                <p className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-100">
                  The first billing2 storage timestamp has been unchanged for{" "}
                  <strong>
                    {formatAge(
                      storageGapPreview.billing2_storage_start_observed_at,
                    )}
                  </strong>
                  , since{" "}
                  {formatTime(
                    storageGapPreview.billing2_storage_start_observed_at,
                  )}
                  .
                </p>
              ) : null}
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-left text-xs">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="whitespace-nowrap px-2 py-2 font-medium">
                        Org
                      </th>
                      <th className="whitespace-nowrap px-2 py-2 font-medium">
                        Last billing1 event
                      </th>
                      <th className="whitespace-nowrap px-2 py-2 font-medium">
                        First billing2 event
                      </th>
                      <th className="whitespace-nowrap px-2 py-2 font-medium">
                        Duration
                      </th>
                      <th className="whitespace-nowrap px-2 py-2 font-medium">
                        Storage
                      </th>
                      <th className="whitespace-nowrap px-2 py-2 font-medium">
                        Byte-hours
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {storageGapPreview.events.map(({ gap_start_at, event }) => (
                      <tr key={event.org_id} className="border-b last:border-0">
                        <td className="whitespace-nowrap px-2 py-2 font-mono">
                          {event.org_id}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {formatTime(gap_start_at)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {formatTime(storageGapPreview.gap_end_at)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {formatDuration(
                            gap_start_at,
                            storageGapPreview.gap_end_at,
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {formatQuantity(event.namespace_bytes, "B")}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {formatQuantity(event.byte_hours ?? 0, "B-hours")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <details className="rounded-md border">
                <summary className="cursor-pointer px-3 py-2 font-medium">
                  Show raw response
                </summary>
                <pre className="max-h-72 overflow-auto border-t bg-slate-950 p-3 font-mono text-xs leading-5 text-slate-200">
                  {JSON.stringify(storageGapPreview, null, 2)}
                </pre>
              </details>
            </div>
          ) : null}
          {actionError ? (
            <p className="text-sm text-red-600">{actionError}</p>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStorageGapPreview(null)}
              disabled={busy !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void mintStorageGap()}
              disabled={busy !== null}
            >
              {busy ? "Minting…" : "Mint storage gap"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={cutoverTarget !== null}
        onOpenChange={(open) => !open && setCutoverTarget(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {cutoverTarget?.rollback_at
                ? "Cut over to billing2 again?"
                : "Cut over to billing2?"}
            </DialogTitle>
            <DialogDescription>
              {cutoverTarget?.rollback_at
                ? `This will set a new cutover time for ${cutoverTarget.cluster_id} and clear its previous rollback and storage-gap state. Billing1 query unit uploads will continue.`
                : `This will stop regular billing1 uploads and enable billing2 uploads for ${cutoverTarget?.cluster_id}. Billing1 query unit uploads will continue.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCutoverTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!cutoverTarget) return;
                const row = cutoverTarget;
                setCutoverTarget(null);
                void run(row, "cutover");
              }}
            >
              {cutoverTarget?.rollback_at
                ? "Cut over to billing2 again"
                : "Cut over to billing2"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={rollbackTarget !== null}
        onOpenChange={(open) => !open && setRollbackTarget(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rollback to billing1?</DialogTitle>
            <DialogDescription>
              This will stop billing2 uploads for {rollbackTarget?.cluster_id}{" "}
              and resume billing1 after the rollback safety delay.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!rollbackTarget) return;
                const row = rollbackTarget;
                setRollbackTarget(null);
                void run(row, "rollback");
              }}
            >
              Rollback to billing1
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
